import { useEffect, useMemo, useState } from 'react'
import api from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Search, Check } from 'lucide-react'

interface TelnyxVoice {
  id: string         // full voice string, e.g. "Telnyx.NaturalHD.celeste"
  name: string
  provider: string   // telnyx | aws | azure | inworld | minimax | resemble | rime | xai
  model: string      // NaturalHD | Natural | Ultra | KokoroTTS | LibriTTS | ArcanaV3 | Polly | ...
  language: string | null
  gender: string | null
  accent: string | null
  age: string | null
  label: string | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Currently saved voice ID — either an alias (molly) or full Telnyx voice string.
  // Used to highlight "currently selected" in the list.
  currentVoiceId: string
  // Receives the full Telnyx voice string (e.g. Telnyx.NaturalHD.celeste).
  // Caller drops it into the custom voice field — same flow as the existing
  // ElevenLabs library modal.
  onSelect: (telnyxVoiceString: string, voice: TelnyxVoice) => void
}

const ALL = '__all__'

export default function TelnyxVoiceLibraryModal({ open, onOpenChange, currentVoiceId, onSelect }: Props) {
  const [voices, setVoices] = useState<TelnyxVoice[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // Default Provider=telnyx — mirrors the Telnyx portal default. Admin can
  // switch to aws/azure/etc. but the common case is "I want a Telnyx native
  // voice for my agent" since that's why this picker exists.
  const [providerFilter, setProviderFilter] = useState<string>('telnyx')
  const [modelFilter, setModelFilter] = useState<string>(ALL)
  const [languageFilter, setLanguageFilter] = useState<string>(ALL)
  const [genderFilter, setGenderFilter] = useState<string>(ALL)

  useEffect(() => {
    if (!open || voices.length > 0) return
    setLoading(true)
    setError(null)
    api.get('/api/platform/telnyx/voices')
      .then(res => {
        setVoices(res.data?.data || [])
      })
      .catch(err => {
        setError(err?.response?.data?.error || err?.message || 'Failed to load Telnyx voice catalog')
      })
      .finally(() => setLoading(false))
  }, [open, voices.length])

  // Reset model when provider changes — model namespaces don't cross
  // providers (NaturalHD belongs to telnyx, ArcanaV3 to rime, etc.).
  useEffect(() => {
    setModelFilter(ALL)
  }, [providerFilter])

  // Provider list, sorted by voice count desc — helps admins see at a
  // glance which providers carry the largest rosters.
  const providers = useMemo(() => {
    const counts = new Map<string, number>()
    voices.forEach(v => counts.set(v.provider, (counts.get(v.provider) || 0) + 1))
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [voices])

  // Models scoped to the active provider.
  const models = useMemo(() => {
    const filtered = providerFilter === ALL ? voices : voices.filter(v => v.provider === providerFilter)
    const counts = new Map<string, number>()
    filtered.forEach(v => counts.set(v.model, (counts.get(v.model) || 0) + 1))
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [voices, providerFilter])

  // Languages scoped to the active provider+model.
  const languages = useMemo(() => {
    let pool = voices
    if (providerFilter !== ALL) pool = pool.filter(v => v.provider === providerFilter)
    if (modelFilter !== ALL) pool = pool.filter(v => v.model === modelFilter)
    const set = new Set<string>()
    pool.forEach(v => { if (v.language) set.add(v.language) })
    return Array.from(set).sort()
  }, [voices, providerFilter, modelFilter])

  const filtered = useMemo(() => {
    return voices.filter(v => {
      if (providerFilter !== ALL && v.provider !== providerFilter) return false
      if (modelFilter !== ALL && v.model !== modelFilter) return false
      if (languageFilter !== ALL && v.language !== languageFilter) return false
      if (genderFilter !== ALL && v.gender !== genderFilter) return false
      if (search) {
        const q = search.toLowerCase()
        const haystack = [v.name, v.id, v.label, v.accent, v.age, v.language].filter(Boolean).join(' ').toLowerCase()
        if (!haystack.includes(q)) return false
      }
      return true
    })
  }, [voices, providerFilter, modelFilter, languageFilter, genderFilter, search])

  const handleSelect = (voice: TelnyxVoice) => {
    onSelect(voice.id, voice)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Browse Telnyx Voice Library</DialogTitle>
          <DialogDescription>
            Live catalog from Telnyx — pick any voice across providers (Telnyx native, AWS Polly, Azure, Inworld, Minimax,
            Resemble, Rime, xAI). Selecting a voice fills the Custom Voice String field with the full ID
            (e.g. <code className="text-[11px] bg-muted px-1 rounded">Telnyx.NaturalHD.celeste</code>) — the TTS Model
            dropdown is ignored for non-ElevenLabs voices since the model is embedded in the ID itself.
          </DialogDescription>
        </DialogHeader>

        {/* Filter row 1: provider + model + language + gender */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Provider</label>
            <Select value={providerFilter} onValueChange={setProviderFilter}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All providers ({voices.length})</SelectItem>
                {providers.map(([p, n]) => (
                  <SelectItem key={p} value={p} className="capitalize">{p} ({n})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Model</label>
            <Select value={modelFilter} onValueChange={setModelFilter}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All models</SelectItem>
                {models.map(([m, n]) => (
                  <SelectItem key={m} value={m}>{m} ({n})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Language</label>
            <Select value={languageFilter} onValueChange={setLanguageFilter}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All languages</SelectItem>
                {languages.map(l => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Gender</label>
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Male">Male</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Search */}
        <div className="relative mt-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search by name, ID, accent, language…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-7 h-8 text-xs"
          />
        </div>

        <div className="flex-1 overflow-y-auto mt-2 -mx-6 px-6">
          {loading && <div className="py-8 text-center text-sm text-muted-foreground">Loading Telnyx voice catalog…</div>}
          {error && <div className="py-8 text-center text-sm text-destructive">{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">No voices match your filters.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filtered.map(voice => {
              const isCurrent = currentVoiceId === voice.id
              return (
                <div
                  key={voice.id}
                  className={`group border rounded-md p-3 hover:border-primary/60 hover:bg-muted/30 cursor-pointer transition-colors ${isCurrent ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => handleSelect(voice)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm capitalize">{voice.name}</span>
                    {isCurrent && <Check className="h-3.5 w-3.5 text-primary" />}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-1">
                    <Badge variant="outline" className="text-[10px] capitalize">{voice.provider}</Badge>
                    <Badge variant="outline" className="text-[10px]">{voice.model}</Badge>
                    {voice.gender && <Badge variant="secondary" className="text-[10px]">{voice.gender}</Badge>}
                    {voice.language && <Badge variant="secondary" className="text-[10px]">{voice.language}</Badge>}
                    {voice.age && <Badge variant="secondary" className="text-[10px]">{voice.age}</Badge>}
                    {voice.accent && <Badge variant="secondary" className="text-[10px]">{voice.accent}</Badge>}
                  </div>
                  {voice.label && (
                    <p className="text-[11px] text-muted-foreground line-clamp-2">{voice.label}</p>
                  )}
                  <p className="text-[9px] font-mono text-muted-foreground mt-1 truncate">{voice.id}</p>
                </div>
              )
            })}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <p className="text-[10px] text-muted-foreground">
            {filtered.length} of {voices.length} voices · Click a voice to use it.
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="h-7 text-xs">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
